import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { Building2, Users, FileText, Plus, Trash2, AlertCircle, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';


function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Por favor, inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function StatCard({ icon: Icon, label, value, color = 'bg-orange-500' }) {
  return (
    <div className="bg-white border border-orange-100 rounded-xl p-4 sm:p-6 transition-all duration-200 hover:shadow-lg hover:shadow-orange-100 hover:-translate-y-1">
      <div className="flex items-center gap-3 sm:gap-4">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 ${color} rounded-xl flex items-center justify-center shadow-md`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">{label}</p>
          <p className="text-2xl sm:text-3xl font-bold text-gray-900">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState({ companies: 0, workers: 0, documents: 0 });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [error, setError] = useState('');
  const [downloadingDossier, setDownloadingDossier] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    contact_name: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, companiesRes] = await Promise.all([
        API.get('/stats/admin'),
        API.get('/companies')
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await API.post('/companies', formData);
      setDialogOpen(false);
      setFormData({ name: '', email: '', password: '', contact_name: '' });
      fetchData();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleDeleteCompany = async () => {
    if (!deleteId) return;
    try {
      await API.delete(`/companies/${deleteId}`);
      setDeleteId(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting company:', err);
    }
  };

  const handleDownloadDossier = async () => {
    setDownloadingDossier(true);
    try {
      const response = await API.get('/dossier/download', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Dossier_Comercial_Urcalportal.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading dossier:', err);
    } finally {
      setDownloadingDossier(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900">
            Panel de Administracion
          </h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Gestiona empresas y supervisa el sistema</p>
        </div>
        <Button
          onClick={handleDownloadDossier}
          disabled={downloadingDossier}
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg shadow-md shadow-orange-200"
          data-testid="download-dossier-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          {downloadingDossier ? 'Generando...' : 'Dossier Comercial'}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <StatCard icon={Building2} label="Empresas" value={stats.companies} />
        <StatCard icon={Users} label="Trabajadores" value={stats.workers} color="bg-orange-400" />
        <StatCard icon={FileText} label="Documentos" value={stats.documents} color="bg-orange-600" />
      </div>

      {/* Companies Section */}
      <div className="bg-white border border-orange-100 rounded-xl">
        <div className="p-4 sm:p-6 border-b border-orange-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
              Empresas
            </h2>
            <p className="text-sm text-gray-500">Gestiona las empresas registradas</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg shadow-md shadow-orange-200" data-testid="create-company-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Empresa
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Crear Nueva Empresa</DialogTitle>
                <DialogDescription>Introduce los datos de la empresa y las credenciales de acceso.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateCompany} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Nombre de la empresa</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Empresa S.L."
                    required
                    data-testid="company-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Persona de contacto</Label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="Juan Garcia"
                    required
                    data-testid="company-contact-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Email de acceso</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="empresa@correo.com"
                    required
                    data-testid="company-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Contrasena</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="........"
                    required
                    data-testid="company-password-input"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white" data-testid="submit-company-btn">
                    Crear Empresa
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-orange-100">
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Empresa</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Contacto</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Trabajadores</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {companies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-400">
                    No hay empresas registradas
                  </TableCell>
                </TableRow>
              ) : (
                companies.map((company) => (
                  <TableRow key={company.id} className="hover:bg-orange-50/50" data-testid={`company-row-${company.id}`}>
                    <TableCell className="font-medium text-gray-900">{company.name}</TableCell>
                    <TableCell className="text-gray-600">{company.contact_name}</TableCell>
                    <TableCell className="text-gray-600">{company.email}</TableCell>
                    <TableCell className="text-right font-semibold text-gray-900">{company.worker_count}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => setDeleteId(company.id)}
                        data-testid={`delete-company-${company.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar empresa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion eliminara la empresa y todos sus trabajadores y documentos asociados. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteCompany}
              data-testid="confirm-delete-company"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
